import { askConfirm } from '@/components/ui/ConfirmHost';
import React, { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getCompositeSubjects, postCompositeSubject, putCompositeSubject, deleteCompositeSubject, getSubjects,
} from '@/api/owner';
import { Plus, Layers, Trash2, X, Edit2, AlertCircle, CheckCircle2, Percent } from 'lucide-react';

const arr = <T,>(src: any): T[] => {
  if (!src) return [];
  if (Array.isArray(src)) return src;
  if (Array.isArray(src.data)) return src.data;
  if (Array.isArray(src.data?.data)) return src.data.data;
  return [];
};

interface ComponentRow {
  id?: number;
  subject_id: number | '';
  weight: string;
}

interface FormState {
  id?: number;
  name: string;
  subject_id: number | '';
  pass_mark: string;
  components: ComponentRow[];
}

const emptyForm: FormState = {
  name: '',
  subject_id: '',
  pass_mark: '',
  components: [{ subject_id: '', weight: '' }],
};

export function CompositeSubjectsPage() {
  const qc = useQueryClient();
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState<FormState>(emptyForm);
  const [error, setError] = useState<string | null>(null);

  const compositesQ = useQuery({
    queryKey: ['owner-composite-subjects'],
    queryFn: () => getCompositeSubjects().then((r: any) => r.data?.data ?? r.data),
  });
  const subjectsQ = useQuery({
    queryKey: ['owner-subjects'],
    queryFn: () => getSubjects({ per_page: 500 }).then((r: any) => r.data?.data ?? r.data),
  });

  const composites = arr<any>(compositesQ.data);
  const subjects = arr<any>(subjectsQ.data);
  const subjectMap = useMemo(() => {
    const m: Record<number, any> = {};
    subjects.forEach((s: any) => { m[s.id] = s; });
    return m;
  }, [subjects]);

  // ─── mutations ───
  const createM = useMutation({
    mutationFn: (body: any) => postCompositeSubject(body),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['owner-composite-subjects'] }); closeForm(); },
    onError: (e: any) => setError(e?.response?.data?.message ?? 'Failed to create composite subject'),
  });

  const updateM = useMutation({
    mutationFn: ({ id, body }: { id: number; body: any }) => putCompositeSubject(id, body),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['owner-composite-subjects'] }); closeForm(); },
    onError: (e: any) => setError(e?.response?.data?.message ?? 'Failed to update composite subject'),
  });

  const deleteM = useMutation({
    mutationFn: (id: number) => deleteCompositeSubject(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['owner-composite-subjects'] }),
    onError: (e: any) => alert(e?.response?.data?.message ?? 'Delete failed'),
  });

  // ─── form helpers ───
  const closeForm = () => { setShowForm(false); setForm(emptyForm); setError(null); };

  const openNew = () => { setForm(emptyForm); setError(null); setShowForm(true); };

  const openEdit = (c: any) => {
    setForm({
      id: c.id,
      name: c.name ?? '',
      subject_id: c.subject_id ?? '',
      pass_mark: c.pass_mark != null ? String(c.pass_mark) : '',
      components: (c.components ?? []).map((comp: any) => ({
        id: comp.id,
        subject_id: comp.subject_id,
        weight: String(comp.weight ?? ''),
      })),
    });
    setError(null);
    setShowForm(true);
  };

  const updateComponent = (idx: number, patch: Partial<ComponentRow>) => {
    setForm(f => ({
      ...f,
      components: f.components.map((c, i) => i === idx ? { ...c, ...patch } : c),
    }));
  };

  const addComponent = () => {
    setForm(f => ({ ...f, components: [...f.components, { subject_id: '', weight: '' }] }));
  };

  const removeComponent = (idx: number) => {
    setForm(f => ({ ...f, components: f.components.filter((_, i) => i !== idx) }));
  };

  const totalWeight = form.components.reduce((s, c) => s + (Number(c.weight) || 0), 0);

  const submitForm = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    // Validate
    if (!form.name.trim()) { setError('Name is required.'); return; }
    if (form.components.length === 0) { setError('Add at least one component subject.'); return; }
    const filled = form.components.filter(c => c.subject_id !== '' && c.weight !== '');
    if (filled.length !== form.components.length) { setError('Every component needs a subject and a weight.'); return; }

    const ids = filled.map(c => Number(c.subject_id));
    if (new Set(ids).size !== ids.length) { setError('You cannot add the same subject twice.'); return; }

    if (totalWeight <= 0) { setError('Total weight must be greater than 0.'); return; }
    if (totalWeight > 100.01) { setError(`Total weight cannot exceed 100 (currently ${totalWeight}).`); return; }

    const body = {
      name: form.name.trim(),
      subject_id: form.subject_id || undefined,
      pass_mark: form.pass_mark ? Number(form.pass_mark) : undefined,
      components: filled.map(c => ({
        subject_id: Number(c.subject_id),
        weight: Number(c.weight),
      })),
    };

    if (form.id) {
      updateM.mutate({ id: form.id, body });
    } else {
      createM.mutate(body);
    }
  };

  return (
    <div className="max-w-[1400px] mx-auto p-6 space-y-6">
      <div className="flex justify-between items-end">
        <div>
          <p className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1">Academics</p>
          <h1 className="text-3xl font-extrabold text-slate-900">Composite Subjects</h1>
          <p className="text-slate-500 text-sm mt-1">
            Combine multiple subjects into one aggregated subject (e.g. English = English Language 60% + Literature 40%)
          </p>
        </div>
        <button onClick={openNew} className="bg-[#0f172a] hover:bg-slate-800 text-white px-4 py-2 rounded-lg font-semibold text-sm shadow-sm flex items-center gap-2">
          <Plus className="w-4 h-4" /> New composite subject
        </button>
      </div>

      {compositesQ.isLoading ? (
        <p className="text-sm text-slate-500 py-8 text-center">Loading…</p>
      ) : composites.length === 0 ? (
        <div className="bg-white p-12 rounded-xl border border-slate-200 text-center">
          <Layers className="w-10 h-10 text-slate-300 mx-auto mb-2" />
          <p className="font-semibold text-slate-700">No composite subjects yet</p>
          <p className="text-sm text-slate-500">Click "New composite subject" to combine subjects like English Language + Literature.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {composites.map((c: any) => {
            const total = (c.components ?? []).reduce((s: number, x: any) => s + Number(x.weight || 0), 0);
            return (
              <div key={c.id} className="bg-white rounded-xl border border-slate-200 shadow-sm p-5">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <h3 className="font-bold text-slate-900 text-lg truncate">{c.name}</h3>
                    <p className="text-xs text-slate-500 mt-0.5">
                      {c.subject_id && subjectMap[c.subject_id]
                        ? `Reports under: ${subjectMap[c.subject_id].name}`
                        : 'Reports under its own name'}
                    </p>
                    {c.pass_mark != null && (
                      <p className="text-xs text-slate-600 mt-1">
                        <Percent className="w-3 h-3 inline" /> Pass mark: <span className="font-semibold">{c.pass_mark}%</span>
                      </p>
                    )}
                  </div>
                  <div className="flex gap-1 flex-shrink-0">
                    <button onClick={() => openEdit(c)}
                      className="p-2 rounded-lg text-slate-500 hover:text-slate-800 hover:bg-slate-100" title="Edit">
                      <Edit2 className="w-4 h-4" />
                    </button>
                    <button onClick={async () => { if (await askConfirm(`Delete composite subject "${c.name}"?`)) deleteM.mutate(c.id); }}
                      className="p-2 rounded-lg text-red-500 hover:text-red-700 hover:bg-red-50" title="Delete">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>

                <ul className="mt-3 space-y-1.5">
                  {(c.components ?? []).map((comp: any) => (
                    <li key={comp.id ?? comp.subject_id} className="flex items-center justify-between text-sm">
                      <span className="text-slate-700">
                        {subjectMap[comp.subject_id]?.name ?? `Subject #${comp.subject_id}`}
                      </span>
                      <span className="font-mono text-slate-500 text-xs">{comp.weight}%</span>
                    </li>
                  ))}
                </ul>

                <div className="mt-3 pt-3 border-t border-slate-100 flex items-center justify-between">
                  <span className="text-xs text-slate-500">Total weight</span>
                  <span className={`text-sm font-bold ${Math.abs(total - 100) < 0.01 ? 'text-green-600' : total > 100 ? 'text-red-600' : 'text-amber-600'}`}>
                    {total}%
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* FORM MODAL */}
      {showForm && (
        <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm z-50 flex items-center justify-center p-4 overflow-y-auto">
          <form onSubmit={submitForm} className="bg-white w-full max-w-2xl rounded-2xl shadow-xl my-8">
            <div className="px-6 py-4 border-b border-slate-200 flex justify-between items-center bg-slate-50 rounded-t-2xl">
              <h3 className="font-bold text-slate-900">{form.id ? 'Edit composite subject' : 'New composite subject'}</h3>
              <button type="button" onClick={closeForm} className="text-slate-400 hover:text-slate-600">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6 space-y-4 max-h-[70vh] overflow-y-auto">
              {error && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-800 flex items-start gap-2">
                  <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" /> {error}
                </div>
              )}

              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1">Composite name *</label>
                <input value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="e.g. English, Science, Business Studies"
                  required
                  className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" />
                <p className="text-xs text-slate-500 mt-1">This is what shows on the student's result sheet.</p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-1">Reports under</label>
                  <select value={form.subject_id}
                    onChange={(e) => setForm({ ...form, subject_id: e.target.value ? Number(e.target.value) : '' })}
                    className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm bg-white">
                    <option value="">Its own name</option>
                    {subjects.map((s: any) => <option key={s.id} value={s.id}>{s.name}</option>)}
                  </select>
                  <p className="text-xs text-slate-500 mt-1">Optional. Pick a parent subject if the composite rolls up into one.</p>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-1">Pass mark (%)</label>
                  <input type="number" min={0} max={100} value={form.pass_mark}
                    onChange={(e) => setForm({ ...form, pass_mark: e.target.value })}
                    placeholder="e.g. 40"
                    className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" />
                  <p className="text-xs text-slate-500 mt-1">Applied when computing the composite result.</p>
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-sm font-semibold text-slate-700">Components *</label>
                  <span className={`text-xs font-semibold ${Math.abs(totalWeight - 100) < 0.01 ? 'text-green-600' : totalWeight > 100 ? 'text-red-600' : 'text-amber-600'}`}>
                    Total: {totalWeight}%
                  </span>
                </div>
                <div className="space-y-2">
                  {form.components.map((comp, idx) => (
                    <div key={idx} className="flex gap-2 items-start">
                      <select value={comp.subject_id}
                        onChange={(e) => updateComponent(idx, { subject_id: e.target.value ? Number(e.target.value) : '' })}
                        className="flex-1 border border-slate-300 rounded-lg px-3 py-2 text-sm bg-white">
                        <option value="">Select subject…</option>
                        {subjects.filter((s: any) => s.id === comp.subject_id || !form.components.some((c, i) => i !== idx && c.subject_id === s.id)).map((s: any) => (
                          <option key={s.id} value={s.id}>{s.name}</option>
                        ))}
                      </select>
                      <div className="relative w-28">
                        <input type="number" min={0} max={100} value={comp.weight}
                          onChange={(e) => updateComponent(idx, { weight: e.target.value })}
                          placeholder="Weight"
                          className="w-full border border-slate-300 rounded-lg px-3 py-2 pr-7 text-sm" />
                        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 text-xs">%</span>
                      </div>
                      <button type="button" disabled={form.components.length === 1}
                        onClick={() => removeComponent(idx)}
                        className="p-2 text-red-500 hover:bg-red-50 rounded-lg disabled:opacity-30" title="Remove">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
                <button type="button" onClick={addComponent}
                  className="mt-2 text-xs font-semibold text-[#0f172a] hover:underline inline-flex items-center gap-1">
                  <Plus className="w-3 h-3" /> Add component
                </button>
              </div>

              {Math.abs(totalWeight - 100) < 0.01 ? (
                <div className="flex items-center gap-1 text-xs text-green-700">
                  <CheckCircle2 className="w-3 h-3" /> Weights sum to 100% — perfect.
                </div>
              ) : totalWeight > 0 && totalWeight < 100 ? (
                <p className="text-xs text-amber-700">
                  ⚠ Weights total {totalWeight}%. Missing {100 - totalWeight}%. Save will allow it — you can adjust later.
                </p>
              ) : totalWeight > 100 ? (
                <p className="text-xs text-red-700">
                  ✕ Weights total {totalWeight}% — exceeds 100%. Reduce weights before saving.
                </p>
              ) : null}
            </div>

            <div className="px-6 py-4 border-t border-slate-200 flex gap-3 bg-slate-50 rounded-b-2xl">
              <button type="button" onClick={closeForm}
                className="flex-1 py-2.5 border border-slate-300 rounded-lg text-slate-700 font-semibold text-sm hover:bg-white">
                Cancel
              </button>
              <button type="submit" disabled={createM.isPending || updateM.isPending}
                className="flex-1 py-2.5 bg-[#0f172a] text-white rounded-lg font-semibold text-sm hover:bg-slate-800 disabled:opacity-50">
                {(createM.isPending || updateM.isPending) ? 'Saving…' : (form.id ? 'Save changes' : 'Create composite')}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
